/**
 * TeacherPortal - Teacher's dedicated portal
 * Dashboard, Attendance, My Classes, Timetable, Assignments
 */
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  LayoutDashboard, BookOpen, Users, Clock, ClipboardList, ClipboardCheck, CalendarRange,
  LogOut, GraduationCap, Loader2, UserCheck, UserX, ChevronLeft, ChevronRight, AlertCircle, FileText,
  Bell, AlertTriangle, Send, Plus, Menu, X, Video, History, Edit2, EyeOff, Archive, Trash2
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose, DialogDescription } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useAuthStore } from '@/lib/store';
import { PortalNotificationBell } from '@/components/school/PortalNotificationBell';
import { getToken } from '@/lib/authToken';
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Phase-1 Teacher Portal sections (iter 403) — modular components that
// each hit a single dedicated /api/school/portal/teacher/* aggregator.
import { TeacherDashboard as TeacherDashboardV2 } from './teacher/TeacherDashboardV2';
import { TeacherSubjects }       from './teacher/TeacherSubjects';
import { TeacherStudents }       from './teacher/TeacherStudents';
import { TeacherExams as TeacherExamsV2 } from './teacher/TeacherExams';
import { TeacherResults }        from './teacher/TeacherResults';
import { TeacherMaterials }      from './teacher/TeacherMaterials';
import { TeacherAnnouncements as TeacherAnnouncementsV2 } from './teacher/TeacherAnnouncements';
import { TeacherBible }          from './teacher/TeacherBible';
import { TeacherLiveClasses }    from './teacher/TeacherLiveClasses';
import { TeacherLessonPlans }    from './teacher/TeacherLessonPlans';
import { TeacherReports as TeacherReportsV2 } from './teacher/TeacherReports';
import { TeacherActivity } from './teacher/TeacherActivity';
import AssignmentGraderDialog from '@/components/school/AssignmentGraderDialog';

// Handle View-As preview token from URL
const initPreviewToken = () => {
  const params = new URLSearchParams(window.location.search);
  const pt = params.get('preview_token');
  if (pt) {
    sessionStorage.setItem('school_preview_token', pt);
    window.history.replaceState({}, '', window.location.pathname);
  }
};
initPreviewToken();
const getEffectiveToken = () => {
  try {
    const previewToken = sessionStorage.getItem('school_preview_token');
    if (previewToken) return previewToken;
  } catch (_e) { /* sessionStorage unavailable */ }
  return getToken();
};
const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api/school${path}`, { ...opts, headers: { 'Authorization': `Bearer ${getEffectiveToken()}`, 'Content-Type': 'application/json', ...opts.headers } });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Request failed'); }
  return res.json();
};
const portalApi = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api/school/portal${path}`, { ...opts, headers: { 'Authorization': `Bearer ${getEffectiveToken()}`, 'Content-Type': 'application/json', ...opts.headers } });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Request failed'); }
  return res.json();
};

// ============== REPORT CONCERN (teacher-initiated) ==============
const ReportConcernButton = ({ tenantId, student }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('behaviour'); // behaviour | safeguarding
  const [form, setForm] = useState({ category: 'disobedience', severity: 'low', description: '', immediate_danger: false });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.description.trim()) { toast.error('Describe what happened'); return; }
    setSaving(true);
    try {
      if (mode === 'behaviour') {
        await api(`/tenants/${tenantId}/behaviour/incidents`, {
          method: 'POST',
          body: JSON.stringify({
            student_id: student.id,
            category: form.category,
            severity: form.severity,
            description: form.description,
            notify_parents: form.severity !== 'low',
          }),
        });
        toast.success('Behaviour incident reported');
      } else {
        await api(`/tenants/${tenantId}/safeguarding/staff-concern`, {
          method: 'POST',
          body: JSON.stringify({
            student_id: student.id,
            narrative: form.description,
            category: 'safeguarding_concern',
            immediate_danger: form.immediate_danger,
          }),
        });
        toast.success('Safeguarding concern filed — DSL team notified');
      }
      setOpen(false);
      setForm({ category: 'disobedience', severity: 'low', description: '', immediate_danger: false });
    } catch (e) {
      toast.error(e.message || 'Failed to submit');
    }
    setSaving(false);
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}
              data-testid={`report-concern-btn-${student.id}`}>
        <AlertTriangle className="h-3.5 w-3.5 mr-1" /> Report
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="report-concern-dialog">
          <DialogHeader>
            <DialogTitle>Report an incident · {student.first_name} {student.last_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex gap-2">
              <Button size="sm" variant={mode === 'behaviour' ? 'default' : 'outline'} onClick={() => setMode('behaviour')}
                      data-testid="report-mode-behaviour">Behaviour / Discipline</Button>
              <Button size="sm" variant={mode === 'safeguarding' ? 'default' : 'outline'} onClick={() => setMode('safeguarding')}
                      data-testid="report-mode-safeguarding">Safeguarding Concern</Button>
            </div>
            {mode === 'behaviour' && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="report-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['disobedience', 'bullying', 'lateness', 'classroom_disruption', 'fighting', 'cheating', 'phone_misuse', 'inappropriate_language', 'other']
                        .map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, ' ')}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Severity</Label>
                  <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                    <SelectTrigger data-testid="report-severity-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['low', 'medium', 'high', 'critical'].map((s) => <SelectItem key={s} value={s}>{s.toUpperCase()}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            {mode === 'safeguarding' && (
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.immediate_danger} onCheckedChange={(v) => setForm({ ...form, immediate_danger: v })}
                        data-testid="report-immediate-danger" />
                <span>The student is in immediate danger</span>
              </label>
            )}
            <div>
              <Label className="text-xs">What happened?</Label>
              <Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                        placeholder="Describe the incident while it's still fresh — time, place, people involved, what you observed."
                        data-testid="report-description-textarea" />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline" size="sm">Cancel</Button></DialogClose>
            <Button size="sm" onClick={submit} disabled={saving} data-testid="report-submit-btn">
              <Send className="h-4 w-4 mr-1" /> {saving ? 'Submitting…' : 'Submit Report'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};


const STATUS_COLORS = {
  present: 'bg-green-100 text-green-700 hover:bg-green-200',
  absent: 'bg-red-100 text-red-700 hover:bg-red-200',
  late: 'bg-amber-100 text-amber-700 hover:bg-amber-200',
  excused: 'bg-blue-100 text-blue-700 hover:bg-blue-200',
};

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

const MENU = [
  { id: 'dashboard',     label: 'Dashboard',         icon: LayoutDashboard },
  { id: 'subjects',      label: 'My Subjects',       icon: BookOpen },
  { id: 'lesson-plans',  label: 'Lesson Plans',      icon: FileText },
  { id: 'students',      label: 'Students',          icon: Users },
  { id: 'attendance',    label: 'Attendance',        icon: Clock },
  { id: 'classes',       label: 'My Classes',        icon: GraduationCap },
  { id: 'timetable',     label: 'Timetable',         icon: CalendarRange },
  { id: 'assignments',   label: 'Assignments',       icon: ClipboardList },
  { id: 'exams',         label: 'Exams',             icon: FileText },
  { id: 'question-bank', label: 'Question Bank',     icon: FileText },
  { id: 'results',       label: 'Results',           icon: AlertCircle },
  { id: 'materials',     label: 'Learning Materials', icon: FileText },
  { id: 'announcements', label: 'Announcements',     icon: Bell },
  { id: 'bible',         label: 'Bible',             icon: BookOpen },
  { id: 'live',          label: 'Live Classes',      icon: Video },
  { id: 'reports',       label: 'Reports',           icon: FileText },
  { id: 'activity',      label: 'Activity',          icon: History },
  { id: 'at-risk',       label: 'At-Risk Students',  icon: AlertTriangle },
  { id: 'report',        label: 'Report Concern',    icon: AlertTriangle },
];

// ============== TEACHER DASHBOARD ==============
const TeacherDashboard = ({ tenantId, onNavigate }) => {
  const { data: profile } = useQuery({
    queryKey: ['teacher-profile'],
    queryFn: () => portalApi('/me'),
  });
  const { data: classes = [] } = useQuery({
    queryKey: ['teacher-my-classes', tenantId],
    queryFn: () => portalApi('/teacher/classes'),
  });
  const { data: assignments = [] } = useQuery({
    queryKey: ['teacher-my-assignments', tenantId],
    queryFn: () => portalApi('/teacher/assignments'),
  });
  const { data: timetable = [] } = useQuery({
    queryKey: ['teacher-my-timetable', tenantId],
    queryFn: () => portalApi('/teacher/timetable'),
  });

  const pendingGrading = assignments.filter(a => a.submission_count > a.graded_count).length;
  const totalStudents = classes.reduce((sum, c) => sum + (c.student_count || 0), 0);

  // My filed reports (behaviour + safeguarding) — shows accountability + freshness
  const { data: myBehaviour = [] } = useQuery({
    queryKey: ['teacher-my-behaviour', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/behaviour/my-incidents`),
    refetchInterval: 60000,
  });
  const { data: mySafeguarding = [] } = useQuery({
    queryKey: ['teacher-my-safeguarding', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/safeguarding/my-reports`),
    refetchInterval: 60000,
  });
  const openReportsCount = (myBehaviour.filter(r => r.status !== 'closed' && r.status !== 'resolved').length
    + mySafeguarding.filter(r => r.status !== 'closed' && r.status !== 'resolved').length);

  // Today's schedule
  const todayDay = DAYS[new Date().getDay() - 1] || 'Monday';
  const todaySlots = timetable.filter(s => s.day_of_week === todayDay)
    .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));

  return (
    <div className="space-y-6" data-testid="teacher-dashboard">
      <div className="flex items-center gap-3 flex-wrap">
        <h2 className="text-xl font-bold text-slate-900">Welcome, {profile?.name || 'Teacher'}</h2>
        {profile?.campus_name && (
          <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200" data-testid="teacher-campus-badge">
            {profile.campus_name}
          </Badge>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4 text-center">
          <Users className="h-5 w-5 mx-auto mb-1 text-indigo-500" />
          <p className="text-2xl font-bold">{classes.length}</p>
          <p className="text-xs text-slate-500">My Classes</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <GraduationCap className="h-5 w-5 mx-auto mb-1 text-emerald-500" />
          <p className="text-2xl font-bold">{totalStudents}</p>
          <p className="text-xs text-slate-500">Total Students</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <AlertCircle className="h-5 w-5 mx-auto mb-1 text-amber-500" />
          <p className="text-2xl font-bold">{pendingGrading}</p>
          <p className="text-xs text-slate-500">Need Grading</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <CalendarRange className="h-5 w-5 mx-auto mb-1 text-rose-500" />
          <p className="text-2xl font-bold">{todaySlots.length}</p>
          <p className="text-xs text-slate-500">Today's Periods</p>
        </CardContent></Card>
      </div>

      {/* Prominent Report Concern CTA — always accessible */}
      <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-rose-50" data-testid="teacher-dashboard-report-cta">
        <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/15 flex items-center justify-center shrink-0">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="font-medium text-sm text-slate-900">Something to report?</p>
              <p className="text-xs text-slate-600">Behaviour, discipline, or safeguarding — file it while it's fresh.</p>
            </div>
          </div>
          <Button size="sm" onClick={() => onNavigate && onNavigate('report')}
                  data-testid="dashboard-report-concern-btn">
            <AlertTriangle className="h-4 w-4 mr-1" /> Report a Concern
          </Button>
        </CardContent>
      </Card>

      {/* My Filed Reports — accountability widget */}
      <Card data-testid="teacher-my-reports-card">
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" /> My Filed Reports <Badge variant="outline" className="text-[10px]">{openReportsCount} open</Badge></CardTitle></CardHeader>
        <CardContent className="space-y-1">
          {myBehaviour.length === 0 && mySafeguarding.length === 0 && (
            <p className="text-sm text-slate-500 py-2">You haven't filed any reports yet. Use the <strong>Report</strong> button next to a student in your class to log a behaviour or safeguarding concern while it's still fresh.</p>
          )}
          {myBehaviour.slice(0, 3).map(r => (
            <div key={r.id} className="flex items-center gap-2 py-1 border-b last:border-0" data-testid={`teacher-report-beh-${r.id}`}>
              <Badge variant="outline" className="text-[10px]">Behaviour</Badge>
              <span className="text-xs flex-1 truncate">{r.student_name || '—'} · {r.category}</span>
              <Badge variant="outline" className="text-[10px] capitalize">{r.severity}</Badge>
              <Badge variant="outline" className="text-[10px] capitalize">{r.status}</Badge>
            </div>
          ))}
          {mySafeguarding.slice(0, 3).map(r => (
            <div key={r.id} className="flex items-center gap-2 py-1 border-b last:border-0" data-testid={`teacher-report-sg-${r.id}`}>
              <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-700 border-rose-200">Safeguarding</Badge>
              <span className="text-xs flex-1 truncate">{r.subject || '—'}</span>
              <Badge variant="outline" className="text-[10px] capitalize">{r.status}</Badge>
            </div>
          ))}
          {(myBehaviour.length > 3 || mySafeguarding.length > 3) && (
            <p className="text-[11px] text-slate-400 pt-1">Showing latest 3 of each. Admin/DSL can see the full queue.</p>
          )}
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base">Today's Schedule ({todayDay})</CardTitle></CardHeader>
          <CardContent>
            {todaySlots.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No classes today</p>
            ) : (
              <div className="space-y-2">
                {todaySlots.map((s, i) => (
                  <div key={s.id || `slot-${i}`} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div>
                      <p className="font-medium text-sm">{s.subject_name}</p>
                      <p className="text-xs text-slate-500">{s.class_name}</p>
                    </div>
                    <Badge variant="outline" className="text-xs">{s.start_time} - {s.end_time}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Recent Assignments</CardTitle></CardHeader>
          <CardContent>
            {assignments.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No assignments yet</p>
            ) : (
              <div className="space-y-2">
                {assignments.slice(0, 5).map(a => (
                  <div key={a.id} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div>
                      <p className="font-medium text-sm">{a.title}</p>
                      <p className="text-xs text-slate-500">{a.subject_name} | Due: {a.due_date || 'No deadline'}</p>
                    </div>
                    <Badge variant={a.graded_count < a.submission_count ? "destructive" : "outline"} className="text-xs">
                      {a.graded_count}/{a.submission_count} graded
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ============== TEACHER ATTENDANCE ==============
const TeacherAttendance = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const today = new Date().toISOString().split('T')[0];
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedDate, setSelectedDate] = useState(today);
  const [records, setRecords] = useState({});

  const { data: classes = [] } = useQuery({
    queryKey: ['teacher-my-classes-att', tenantId],
    queryFn: () => portalApi('/teacher/classes'),
  });

  const { data: attendanceData, isLoading } = useQuery({
    queryKey: ['teacher-attendance', tenantId, selectedClass, selectedDate],
    queryFn: () => api(`/tenants/${tenantId}/attendance?class_id=${selectedClass}&date=${selectedDate}`),
    enabled: !!selectedClass,
  });

  React.useEffect(() => {
    if (attendanceData?.records) {
      const initial = {};
      attendanceData.records.forEach(r => { initial[r.id] = r.status || 'present'; });
      setRecords(initial);
    }
  }, [attendanceData]);

  const markMutation = useMutation({
    mutationFn: (payload) => api(`/tenants/${tenantId}/attendance`, { method: 'POST', body: JSON.stringify(payload) }),
    onSuccess: (data) => {
      toast.success(`Saved: ${data.present}P ${data.absent}A ${data.late}L`);
      queryClient.invalidateQueries({ queryKey: ['teacher-attendance'] });
    },
    onError: (e) => toast.error(e.message),
  });

  const handleSave = () => {
    if (!selectedClass || !attendanceData?.records) return;
    markMutation.mutate({
      class_id: selectedClass, date: selectedDate,
      records: attendanceData.records.map(r => ({ student_id: r.id, status: records[r.id] || 'present' })),
    });
  };

  return (
    <div className="space-y-4" data-testid="teacher-attendance">
      <h2 className="text-lg font-semibold">Mark Attendance</h2>
      <div className="flex gap-3 flex-wrap">
        <Select value={selectedClass} onValueChange={setSelectedClass}>
          <SelectTrigger className="w-full max-w-[200px]"><SelectValue placeholder="Select class" /></SelectTrigger>
          <SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} className="w-full max-w-[180px]" />
        {selectedClass && (
          <>
            <Button variant="outline" size="sm" onClick={() => { const u = {}; attendanceData?.records?.forEach(r => { u[r.id] = 'present'; }); setRecords(u); }}>
              <UserCheck className="h-3.5 w-3.5 mr-1" /> All Present
            </Button>
            <Button variant="outline" size="sm" onClick={() => { const u = {}; attendanceData?.records?.forEach(r => { u[r.id] = 'absent'; }); setRecords(u); }}>
              <UserX className="h-3.5 w-3.5 mr-1" /> All Absent
            </Button>
          </>
        )}
      </div>
      {selectedClass && !isLoading && attendanceData?.records?.length > 0 && (
        <>
          <Table>
            <TableHeader><TableRow>
              <TableHead className="w-[30px]">#</TableHead>
              <TableHead>Student</TableHead>
              <TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {attendanceData.records.map((s, i) => (
                <TableRow key={s.id}>
                  <TableCell className="text-slate-400">{i + 1}</TableCell>
                  <TableCell className="font-medium">{s.first_name} {s.last_name}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {['present', 'absent', 'late', 'excused'].map(st => (
                        <button key={st} onClick={() => setRecords(p => ({ ...p, [s.id]: st }))}
                          className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${records[s.id] === st ? STATUS_COLORS[st] : 'bg-slate-100 text-slate-400'}`}>
                          {st}
                        </button>
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Button onClick={handleSave} disabled={markMutation.isPending} data-testid="teacher-save-attendance">
            {markMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Save Attendance
          </Button>
        </>
      )}
      {selectedClass && !isLoading && (!attendanceData?.records || attendanceData.records.length === 0) && (
        <Card className="border-dashed"><CardContent className="py-8 text-center text-slate-400">No students enrolled in this class</CardContent></Card>
      )}
    </div>
  );
};

// ============== MY CLASSES ==============
const TeacherClasses = ({ tenantId }) => {
  const [selectedClass, setSelectedClass] = useState(null);

  const { data: classes = [], isLoading } = useQuery({
    queryKey: ['teacher-my-classes-list', tenantId],
    queryFn: () => portalApi('/teacher/classes'),
  });

  const { data: students = [] } = useQuery({
    queryKey: ['teacher-class-students', tenantId, selectedClass],
    queryFn: () => portalApi(`/teacher/classes/${selectedClass}/students`),
    enabled: !!selectedClass,
  });

  if (isLoading) return <Loader2 className="h-6 w-6 animate-spin mx-auto" />;

  return (
    <div className="space-y-4" data-testid="teacher-classes">
      <h2 className="text-lg font-semibold">My Classes</h2>
      {classes.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-8 text-center text-slate-400">No classes assigned</CardContent></Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {classes.map(c => (
            <Card key={c.id} className={cn("cursor-pointer transition-shadow hover:shadow-md", selectedClass === c.id && "ring-2 ring-indigo-500")}
              onClick={() => setSelectedClass(selectedClass === c.id ? null : c.id)} data-testid={`teacher-class-card-${c.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold">{c.name}</h3>
                  {c.is_class_teacher && <Badge className="bg-indigo-100 text-indigo-700 text-xs">Class Teacher</Badge>}
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Users className="h-3.5 w-3.5" />
                  <span>{c.student_count} students</span>
                </div>
                {c.grade_level && <p className="text-xs text-slate-400 mt-1">Grade: {c.grade_level}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {selectedClass && (
        <Card>
          <CardHeader><CardTitle className="text-base">Students in {classes.find(c => c.id === selectedClass)?.name}</CardTitle></CardHeader>
          <CardContent>
            {students.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No students enrolled</p>
            ) : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead className="w-[30px]">#</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Admission #</TableHead>
                  <TableHead>Gender</TableHead>
                  <TableHead className="w-full max-w-[160px] text-right">Action</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {students.map((s, i) => (
                    <TableRow key={s.id} data-testid={`teacher-student-row-${s.id}`}>
                      <TableCell className="text-slate-400">{i + 1}</TableCell>
                      <TableCell className="font-medium">{s.first_name} {s.last_name}</TableCell>
                      <TableCell>{s.admission_number}</TableCell>
                      <TableCell className="capitalize">{s.gender || '-'}</TableCell>
                      <TableCell className="text-right">
                        <ReportConcernButton tenantId={tenantId} student={s} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ============== TIMETABLE ==============
const TeacherTimetable = ({ tenantId }) => {
  const { data: slots = [], isLoading } = useQuery({
    queryKey: ['teacher-my-timetable-view', tenantId],
    queryFn: () => portalApi('/teacher/timetable'),
  });

  if (isLoading) return <Loader2 className="h-6 w-6 animate-spin mx-auto" />;

  const grouped = {};
  DAYS.forEach(d => { grouped[d] = []; });
  slots.forEach(s => {
    const day = s.day_of_week || 'Monday';
    if (grouped[day]) grouped[day].push(s);
  });
  Object.values(grouped).forEach(arr => arr.sort((a, b) => (a.start_time || '').localeCompare(b.start_time || '')));

  return (
    <div className="space-y-4" data-testid="teacher-timetable">
      <h2 className="text-lg font-semibold">My Timetable</h2>
      {slots.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-8 text-center text-slate-400">No timetable slots assigned</CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {DAYS.map(day => (
            <Card key={day}>
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm font-semibold">{day}</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                {grouped[day].length === 0 ? (
                  <p className="text-xs text-slate-400">No classes</p>
                ) : (
                  <div className="space-y-1.5">
                    {grouped[day].map((s, i) => (
                      <div key={s.id || `sched-${day}-${i}`} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                        <div>
                          <p className="font-medium text-sm">{s.subject_name || 'Subject'}</p>
                          <p className="text-xs text-slate-500">{s.class_name}</p>
                        </div>
                        <Badge variant="outline" className="text-xs font-mono">{s.start_time} - {s.end_time}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ============== QUESTION BANK (iter-424) ==============
// Teachers manage a standalone bank of questions across their assigned
// subjects — independent from the Exams tab (which links a question to a
// specific exam).
const TeacherQuestionBank = ({ tenantId }) => {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [type, setType] = useState('');
  const [mineOnly, setMineOnly] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const { data: subjects = [] } = useQuery({
    queryKey: ['teacher-subjects-qb', tenantId],
    queryFn: () => portalApi('/teacher/subjects/mine'),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['teacher-qbank', tenantId, q, subjectId, difficulty, type, mineOnly],
    queryFn: () => portalApi(`/teacher/question-bank?${new URLSearchParams({
      ...(q ? { q } : {}),
      ...(subjectId ? { subject_id: subjectId } : {}),
      ...(difficulty ? { difficulty } : {}),
      ...(type ? { question_type: type } : {}),
      mine_only: mineOnly ? 'true' : 'false',
      limit: 200,
    }).toString()}`),
  });
  const questions = data?.questions || [];

  const del = useMutation({
    mutationFn: (id) => portalApi(`/teacher/question-bank/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries({ queryKey: ['teacher-qbank'] }); },
    onError: (e) => toast.error(e.message || 'Delete failed'),
  });

  const openNew = () => { setEditing(null); setEditorOpen(true); };
  const openEdit = (qq) => { setEditing(qq); setEditorOpen(true); };

  return (
    <div className="space-y-3" data-testid="teacher-question-bank">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-semibold">Question Bank</h2>
        <Button size="sm" onClick={openNew} data-testid="new-question-btn"><Plus className="h-4 w-4 mr-1" /> New question</Button>
      </div>
      <Card>
        <CardContent className="p-3 grid grid-cols-1 md:grid-cols-5 gap-2">
          <Input placeholder="Search text / topic / tag" value={q} onChange={(e) => setQ(e.target.value)} data-testid="qb-search" />
          <Select value={subjectId || 'ALL'} onValueChange={(v) => setSubjectId(v === 'ALL' ? '' : v)}>
            <SelectTrigger data-testid="qb-filter-subject"><SelectValue placeholder="All subjects" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All subjects</SelectItem>
              {subjects.map((s) => <SelectItem key={s.subject_id || s.id} value={s.subject_id || s.id}>{s.name || s.subject_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={type || 'ALL'} onValueChange={(v) => setType(v === 'ALL' ? '' : v)}>
            <SelectTrigger data-testid="qb-filter-type"><SelectValue placeholder="All types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All types</SelectItem>
              <SelectItem value="mcq_single">MCQ (single)</SelectItem>
              <SelectItem value="mcq_multi">MCQ (multi)</SelectItem>
              <SelectItem value="true_false">True/False</SelectItem>
              <SelectItem value="short_answer">Short answer</SelectItem>
              <SelectItem value="long_essay">Long essay</SelectItem>
              <SelectItem value="fill_blank">Fill blank</SelectItem>
            </SelectContent>
          </Select>
          <Select value={difficulty || 'ALL'} onValueChange={(v) => setDifficulty(v === 'ALL' ? '' : v)}>
            <SelectTrigger data-testid="qb-filter-difficulty"><SelectValue placeholder="Any difficulty" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Any difficulty</SelectItem>
              <SelectItem value="easy">Easy</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="hard">Hard</SelectItem>
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-xs">
            <Switch checked={mineOnly} onCheckedChange={setMineOnly} data-testid="qb-mine-only" />
            Only mine
          </label>
        </CardContent>
      </Card>
      {isLoading ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : (
        questions.length === 0 ? (
          <Card className="border-dashed"><CardContent className="py-10 text-center text-slate-400">
            No questions yet. Click <b>New question</b> to add one to the bank.
          </CardContent></Card>
        ) : (
          <div className="space-y-2">
            {questions.map((qq) => {
              const subj = subjects.find((s) => (s.subject_id || s.id) === qq.subject_id);
              const canEdit = qq.is_mine;
              return (
                <Card key={qq.id} data-testid={`qb-row-${qq.id}`}>
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm">{qq.question_text}</p>
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          <Badge variant="outline" className="text-[10px]">{qq.question_type}</Badge>
                          <Badge variant="outline" className="text-[10px]">{qq.difficulty || 'medium'}</Badge>
                          <Badge variant="outline" className="text-[10px]">{qq.marks || 1} mk</Badge>
                          {subj && <Badge variant="outline" className="text-[10px]">{subj.name || subj.subject_name}</Badge>}
                          {qq.topic && <Badge variant="outline" className="text-[10px]">{qq.topic}</Badge>}
                          {qq.usage_count > 0 && <Badge className="text-[10px] bg-indigo-100 text-indigo-700">used in {qq.usage_count} exam(s)</Badge>}
                          {!qq.is_mine && <Badge className="text-[10px] bg-slate-100 text-slate-500">read-only</Badge>}
                        </div>
                      </div>
                      {canEdit && (
                        <div className="flex gap-1 shrink-0">
                          <Button size="sm" variant="ghost" onClick={() => openEdit(qq)} data-testid={`qb-edit-${qq.id}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => {
                            if (qq.usage_count > 0) { toast.error('Linked to an exam. Remove from exam first.'); return; }
                            if (window.confirm('Delete this question?')) del.mutate(qq.id);
                          }} data-testid={`qb-del-${qq.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )
      )}
      <QuestionBankEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        initial={editing}
        subjects={subjects}
        onSaved={() => { setEditorOpen(false); qc.invalidateQueries({ queryKey: ['teacher-qbank'] }); }}
      />
    </div>
  );
};

const QuestionBankEditorDialog = ({ open, onOpenChange, initial, subjects, onSaved }) => {
  const emptyForm = React.useCallback(() => ({
    subject_id: subjects?.[0]?.subject_id || subjects?.[0]?.id || '',
    question_type: 'mcq_single',
    question_text: '',
    options: ['', '', '', ''],
    correct_answer: '',
    correct_answers: [],
    marks: 1,
    difficulty: 'medium',
    explanation: '',
    tags: [],
    topic: '',
    is_scripture: false,
  }), [subjects]);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (initial) {
      setForm({
        subject_id: initial.subject_id || (subjects?.[0]?.subject_id || subjects?.[0]?.id || ''),
        question_type: initial.question_type || 'mcq_single',
        question_text: initial.question_text || '',
        options: initial.options?.length ? initial.options : ['', '', '', ''],
        correct_answer: initial.correct_answer || '',
        correct_answers: initial.correct_answers || [],
        marks: initial.marks || 1,
        difficulty: initial.difficulty || 'medium',
        explanation: initial.explanation || '',
        tags: initial.tags || [],
        topic: initial.topic || '',
        is_scripture: !!initial.is_scripture,
      });
    } else if (open) {
      setForm(emptyForm());
    }
  }, [initial, open, subjects, emptyForm]);

  const usesOptions = ['mcq_single', 'mcq_multi'].includes(form.question_type);
  const isMulti = form.question_type === 'mcq_multi';
  const isTF = form.question_type === 'true_false';

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
  const setOption = (idx, v) => setForm((prev) => {
    const opts = [...prev.options]; opts[idx] = v; return { ...prev, options: opts };
  });

  const submit = async () => {
    if (!form.question_text.trim()) { toast.error('Question text is required'); return; }
    if (!form.subject_id) { toast.error('Pick a subject'); return; }
    const payload = { ...form, marks: Number(form.marks) || 1,
                      options: usesOptions ? form.options.filter((o) => o.trim()) : [] };
    setSaving(true);
    try {
      if (initial?.id) {
        await portalApi(`/teacher/question-bank/${initial.id}`, {
          method: 'PATCH', body: JSON.stringify(payload),
        });
      } else {
        await portalApi('/teacher/question-bank', {
          method: 'POST', body: JSON.stringify(payload),
        });
      }
      toast.success(initial ? 'Question updated' : 'Question added');
      onSaved?.();
    } catch (e) { toast.error(e.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="qb-editor-dialog">
        <DialogHeader>
          <DialogTitle>{initial ? 'Edit question' : 'New question'}</DialogTitle>
          <DialogDescription>Bank questions can be reused in exams later.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Subject *</Label>
              <Select value={form.subject_id} onValueChange={(v) => setField('subject_id', v)}>
                <SelectTrigger data-testid="qb-form-subject"><SelectValue placeholder="Select subject" /></SelectTrigger>
                <SelectContent>{subjects.map((s) => <SelectItem key={s.subject_id || s.id} value={s.subject_id || s.id}>{s.name || s.subject_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={form.question_type} onValueChange={(v) => setField('question_type', v)}>
                <SelectTrigger data-testid="qb-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mcq_single">MCQ (single)</SelectItem>
                  <SelectItem value="mcq_multi">MCQ (multi)</SelectItem>
                  <SelectItem value="true_false">True/False</SelectItem>
                  <SelectItem value="short_answer">Short answer</SelectItem>
                  <SelectItem value="long_essay">Long essay</SelectItem>
                  <SelectItem value="fill_blank">Fill blank</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Question text *</Label>
            <Textarea rows={2} value={form.question_text} onChange={(e) => setField('question_text', e.target.value)} data-testid="qb-form-text" />
          </div>
          {usesOptions && (
            <div className="space-y-1">
              <Label className="text-xs">Options (blank rows are ignored)</Label>
              {form.options.map((opt, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input value={opt} onChange={(e) => setOption(idx, e.target.value)} placeholder={`Option ${idx + 1}`} data-testid={`qb-form-opt-${idx}`} />
                  {isMulti ? (
                    <label className="flex items-center text-xs gap-1">
                      <input type="checkbox" checked={form.correct_answers.includes(opt)} onChange={(e) => {
                        setField('correct_answers', e.target.checked ? [...form.correct_answers, opt] : form.correct_answers.filter((x) => x !== opt));
                      }} data-testid={`qb-form-correct-multi-${idx}`} /> correct
                    </label>
                  ) : (
                    <label className="flex items-center text-xs gap-1">
                      <input type="radio" name="qb-correct" checked={form.correct_answer === opt} onChange={() => setField('correct_answer', opt)} data-testid={`qb-form-correct-${idx}`} /> correct
                    </label>
                  )}
                </div>
              ))}
            </div>
          )}
          {isTF && (
            <div>
              <Label className="text-xs">Correct answer</Label>
              <Select value={form.correct_answer || 'true'} onValueChange={(v) => setField('correct_answer', v)}>
                <SelectTrigger data-testid="qb-form-tf"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="true">True</SelectItem><SelectItem value="false">False</SelectItem></SelectContent>
              </Select>
            </div>
          )}
          {['short_answer', 'fill_blank'].includes(form.question_type) && (
            <div>
              <Label className="text-xs">Expected answer</Label>
              <Input value={form.correct_answer || ''} onChange={(e) => setField('correct_answer', e.target.value)} data-testid="qb-form-answer" />
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <div><Label className="text-xs">Marks</Label><Input type="number" value={form.marks} onChange={(e) => setField('marks', e.target.value)} data-testid="qb-form-marks" /></div>
            <div>
              <Label className="text-xs">Difficulty</Label>
              <Select value={form.difficulty} onValueChange={(v) => setField('difficulty', v)}>
                <SelectTrigger data-testid="qb-form-difficulty"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="easy">Easy</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="hard">Hard</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Topic</Label><Input value={form.topic || ''} onChange={(e) => setField('topic', e.target.value)} data-testid="qb-form-topic" /></div>
          </div>
          <div>
            <Label className="text-xs">Explanation (shown after grading)</Label>
            <Textarea rows={2} value={form.explanation || ''} onChange={(e) => setField('explanation', e.target.value)} data-testid="qb-form-explanation" />
          </div>
          <label className="flex items-center gap-2 text-xs">
            <Switch checked={form.is_scripture} onCheckedChange={(v) => setField('is_scripture', v)} data-testid="qb-form-scripture" />
            Scripture-based question
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="qb-form-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            {initial ? 'Save changes' : 'Add to bank'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ============== ASSIGNMENTS ==============
// Iter-418: teachers can now Create, Edit, Publish, Unpublish, Archive
// assignments directly. Backend already exposes POST /teacher/assignments and
// PATCH /teacher/assignments/{id} — this file previously only offered a
// grading dialog with a dead-end empty state saying "use the admin panel".
const TeacherAssignments = ({ tenantId }) => {
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [gradingSub, setGradingSub] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);  // assignment being edited, or null for "new"
  const qc = useQueryClient();

  const { data: assignments = [], isLoading } = useQuery({
    queryKey: ['teacher-my-assignments-list', tenantId],
    queryFn: () => portalApi('/teacher/assignments'),
  });

  // Assigned subjects + classes — used to gate the editor form
  const { data: mySubjects = [] } = useQuery({
    queryKey: ['teacher-my-subjects', tenantId],
    queryFn: () => portalApi('/teacher/subjects/mine'),
  });
  const { data: myClasses = [] } = useQuery({
    queryKey: ['teacher-my-classes', tenantId],
    queryFn: () => portalApi('/teacher/classes/mine'),
  });

  const { data: submissions = [] } = useQuery({
    queryKey: ['teacher-assignment-subs', tenantId, selectedAssignment],
    queryFn: () => portalApi(`/teacher/assignments/${selectedAssignment}/submissions`),
    enabled: !!selectedAssignment,
  });

  const patch = useMutation({
    mutationFn: ({ id, body }) => portalApi(`/teacher/assignments/${id}`, {
      method: 'PATCH', body: JSON.stringify(body),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['teacher-my-assignments-list', tenantId] });
      toast.success('Assignment updated');
    },
    onError: (e) => toast.error(e.message || 'Update failed'),
  });

  const openNew = () => { setEditing(null); setEditorOpen(true); };
  const openEdit = (a) => { setEditing(a); setEditorOpen(true); };
  const togglePublish = (a) => patch.mutate({
    id: a.id, body: { status: a.status === 'published' ? 'draft' : 'published' },
  });
  const archive = (a) => patch.mutate({ id: a.id, body: { status: 'archived' } });
  const del = useMutation({
    mutationFn: (id) => portalApi(`/teacher/assignments/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['teacher-my-assignments-list', tenantId] }); toast.success('Assignment deleted'); },
    onError: (e) => toast.error(e.message || 'Delete failed'),
  });

  if (isLoading) return <Loader2 className="h-6 w-6 animate-spin mx-auto" />;

  return (
    <div className="space-y-4" data-testid="teacher-assignments">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">My Assignments</h2>
        <Button size="sm" onClick={openNew} data-testid="new-assignment-btn">
          <Plus className="h-4 w-4 mr-1" /> New assignment
        </Button>
      </div>

      {assignments.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-8 text-center text-slate-400">
          No assignments yet. Click <b>New assignment</b> to create one.
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {assignments.map(a => (
            <Card key={a.id} className={cn("transition-shadow hover:shadow-md", selectedAssignment === a.id && "ring-2 ring-indigo-500")}
              data-testid={`teacher-assignment-${a.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex-1 min-w-0 cursor-pointer"
                       onClick={() => setSelectedAssignment(selectedAssignment === a.id ? null : a.id)}>
                    <h3 className="font-semibold text-sm">{a.title}</h3>
                    <p className="text-xs text-slate-500">
                      {a.subject_name || a.subject_id} · Due: {a.due_at || a.due_date || 'No deadline'} · Max: {a.max_marks || 100} marks
                    </p>
                  </div>
                  <div className="flex gap-1.5 items-center">
                    <Badge className={cn(
                      "text-xs",
                      a.status === 'published' ? 'bg-emerald-100 text-emerald-700'
                        : a.status === 'archived' ? 'bg-slate-100 text-slate-500'
                        : 'bg-amber-100 text-amber-700'
                    )}>{a.status || 'draft'}</Badge>
                    <Badge variant="outline" className="text-xs">{a.submission_count || 0} submitted</Badge>
                    <Badge variant={(a.graded_count || 0) < (a.submission_count || 0) ? "destructive" : "secondary"} className="text-xs">
                      {a.graded_count || 0} graded
                    </Badge>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(a)}
                            data-testid={`edit-assignment-${a.id}`}>
                      <Edit2 className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => togglePublish(a)}
                            disabled={a.status === 'archived' || patch.isPending}
                            data-testid={`publish-assignment-${a.id}`}>
                      {a.status === 'published' ? <><EyeOff className="h-3.5 w-3.5 mr-1" />Unpublish</> : <><Send className="h-3.5 w-3.5 mr-1" />Publish</>}
                    </Button>
                    {a.status !== 'archived' && (
                      <Button size="sm" variant="ghost" onClick={() => archive(a)}
                              disabled={patch.isPending}
                              data-testid={`archive-assignment-${a.id}`}>
                        <Archive className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => {
                      if ((a.submission_count || 0) > 0) { toast.error('Cannot delete — submissions exist. Archive instead.'); return; }
                      if (window.confirm(`Delete assignment "${a.title}"? This cannot be undone.`)) del.mutate(a.id);
                    }} disabled={del.isPending} className="text-red-600 hover:text-red-700"
                            data-testid={`delete-assignment-${a.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {selectedAssignment && (
        <Card>
          <CardHeader><CardTitle className="text-base">Submissions for: {assignments.find(a => a.id === selectedAssignment)?.title}</CardTitle></CardHeader>
          <CardContent>
            {submissions.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No submissions yet</p>
            ) : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Student</TableHead>
                  <TableHead>Admission #</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Marks</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {submissions.map((s, i) => (
                    <TableRow key={s.id} data-testid={`grade-row-${s.id}`}>
                      <TableCell className="text-slate-400">{i + 1}</TableCell>
                      <TableCell className="font-medium">{s.student_name}</TableCell>
                      <TableCell>{s.admission_number}</TableCell>
                      <TableCell>
                        <Badge className={s.status === 'graded' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>{s.status}</Badge>
                      </TableCell>
                      <TableCell>{s.marks != null ? s.marks : '-'}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline"
                                onClick={() => setGradingSub(s)}
                                data-testid={`grade-btn-${s.id}`}>
                          <ClipboardCheck className="h-3.5 w-3.5 mr-1" /> Grade
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      <AssignmentGraderDialog
        assignment={assignments.find(a => a.id === selectedAssignment)}
        submission={gradingSub}
        open={!!gradingSub}
        onOpenChange={(v) => !v && setGradingSub(null)}
      />

      <AssignmentEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        initial={editing}
        subjects={mySubjects}
        classes={myClasses}
        onSaved={() => {
          setEditorOpen(false);
          qc.invalidateQueries({ queryKey: ['teacher-my-assignments-list', tenantId] });
        }}
      />
    </div>
  );
};

// New in iter-418 — inline editor dialog for create + edit.
// Iter-424 — added attachments upload + status pill.
const AssignmentEditorDialog = ({ open, onOpenChange, initial, subjects, classes, onSaved }) => {
  const [form, setForm] = useState({
    title: '', subject_id: '', class_ids: [], instructions: '',
    open_at: '', due_at: '', max_marks: 100, attachments: [],
    allow_late: true, allow_resubmission: false, status: 'draft',
  });
  const [uploading, setUploading] = useState(false);

  React.useEffect(() => {
    if (initial) {
      setForm({
        title: initial.title || '',
        subject_id: initial.subject_id || '',
        class_ids: initial.class_ids || [],
        instructions: initial.instructions || '',
        open_at: initial.open_at || '',
        due_at: initial.due_at || '',
        max_marks: initial.max_marks || 100,
        attachments: initial.attachments || [],
        allow_late: initial.allow_late !== false,
        allow_resubmission: !!initial.allow_resubmission,
        status: initial.status || 'draft',
      });
    } else if (open) {
      // Fresh form when opening for "new"
      setForm({
        title: '', subject_id: (subjects?.[0]?.id || subjects?.[0]?.subject_id || ''),
        class_ids: [], instructions: '', open_at: '', due_at: '',
        max_marks: 100, attachments: [], allow_late: true, allow_resubmission: false, status: 'draft',
      });
    }
  }, [initial, open, subjects]);

  const [saving, setSaving] = useState(false);

  const uploadFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    try {
      const newOnes = [];
      for (const file of fileList) {
        const fd = new FormData();
        fd.append('file', file);
        const res = await fetch(`${API_URL}/api/school/portal/teacher/assignments/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${getToken()}` },
          body: fd,
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.detail || `Upload failed for ${file.name}`);
        }
        const { attachment } = await res.json();
        newOnes.push(attachment);
      }
      setForm((prev) => ({ ...prev, attachments: [...(prev.attachments || []), ...newOnes] }));
      toast.success(`Uploaded ${newOnes.length} file(s)`);
    } catch (e) { toast.error(e.message || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const removeAttachment = (idx) => setForm((prev) => ({
    ...prev, attachments: (prev.attachments || []).filter((_, i) => i !== idx),
  }));

  const submit = async (publish = false) => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!form.subject_id) { toast.error('Select a subject'); return; }
    const payload = {
      ...form,
      max_marks: Number(form.max_marks) || 100,
      status: publish ? 'published' : form.status,
    };
    setSaving(true);
    try {
      if (initial?.id) {
        await portalApi(`/teacher/assignments/${initial.id}`, {
          method: 'PATCH', body: JSON.stringify(payload),
        });
        toast.success('Assignment updated');
      } else {
        await portalApi('/teacher/assignments', {
          method: 'POST', body: JSON.stringify(payload),
        });
        toast.success(publish ? 'Assignment published' : 'Draft saved');
      }
      onSaved?.();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="assignment-editor-dialog">
        <DialogHeader>
          <DialogTitle>{initial ? 'Edit assignment' : 'New assignment'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Title *</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                   data-testid="assignment-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Subject *</Label>
              <Select value={form.subject_id} onValueChange={(v) => setForm({ ...form, subject_id: v })}>
                <SelectTrigger data-testid="assignment-subject"><SelectValue placeholder="Select subject" /></SelectTrigger>
                <SelectContent>
                  {(subjects || []).map(s => (
                    <SelectItem key={s.id || s.subject_id} value={s.id || s.subject_id}>
                      {s.name || s.subject_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Max marks</Label>
              <Input type="number" value={form.max_marks}
                     onChange={(e) => setForm({ ...form, max_marks: e.target.value })}
                     data-testid="assignment-max-marks" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Classes (leave empty for subject-wide)</Label>
            <div className="flex flex-wrap gap-1.5 border rounded p-2 min-h-[42px]">
              {(classes || []).map(c => {
                const cid = c.id || c.class_id;
                const on = form.class_ids.includes(cid);
                return (
                  <Badge key={cid} variant={on ? 'default' : 'outline'} className="cursor-pointer text-xs"
                         onClick={() => setForm({
                           ...form,
                           class_ids: on ? form.class_ids.filter(x => x !== cid) : [...form.class_ids, cid],
                         })}
                         data-testid={`assignment-class-${cid}`}>
                    {c.name || c.class_name}
                  </Badge>
                );
              })}
              {(classes || []).length === 0 && (
                <span className="text-xs text-slate-400">No classes assigned yet</span>
              )}
            </div>
          </div>
          <div>
            <Label className="text-xs">Instructions</Label>
            <Textarea rows={4} value={form.instructions}
                      onChange={(e) => setForm({ ...form, instructions: e.target.value })}
                      data-testid="assignment-instructions" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Open date/time</Label>
              <Input type="datetime-local" value={form.open_at}
                     onChange={(e) => setForm({ ...form, open_at: e.target.value })}
                     data-testid="assignment-open-at" />
            </div>
            <div>
              <Label className="text-xs">Due date/time</Label>
              <Input type="datetime-local" value={form.due_at}
                     onChange={(e) => setForm({ ...form, due_at: e.target.value })}
                     data-testid="assignment-due-at" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Attachments (PDF/Doc/Image · max 25 MB each)</Label>
            <div className="border rounded p-2 space-y-1.5">
              {(form.attachments || []).length === 0 && (
                <p className="text-xs text-slate-400">No files attached yet.</p>
              )}
              {(form.attachments || []).map((att, idx) => (
                <div key={idx} className="flex items-center gap-2 text-xs border rounded px-2 py-1" data-testid={`assignment-att-${idx}`}>
                  <FileText className="h-3.5 w-3.5 text-indigo-600" />
                  <a href={`${API_URL}${att.url}`} target="_blank" rel="noopener noreferrer" className="flex-1 truncate hover:underline">{att.name}</a>
                  <span className="text-slate-400">{Math.round((att.size_bytes || 0) / 1024)} KB</span>
                  <button type="button" onClick={() => removeAttachment(idx)} className="text-red-500" data-testid={`assignment-att-remove-${idx}`}><Trash2 className="h-3 w-3" /></button>
                </div>
              ))}
              <input type="file" multiple onChange={(e) => uploadFiles(Array.from(e.target.files || []))} disabled={uploading} data-testid="assignment-att-input" />
              {uploading && <p className="text-xs text-slate-500"><Loader2 className="h-3 w-3 inline mr-1 animate-spin" /> Uploading…</p>}
            </div>
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.allow_late}
                      onCheckedChange={(v) => setForm({ ...form, allow_late: v })}
                      data-testid="assignment-allow-late" />
              Allow late submissions
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.allow_resubmission}
                      onCheckedChange={(v) => setForm({ ...form, allow_resubmission: v })}
                      data-testid="assignment-allow-resubmission" />
              Allow resubmission
            </label>
          </div>
        </div>
        <DialogFooter className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button variant="outline" onClick={() => submit(false)} disabled={saving}
                  data-testid="save-draft-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Save draft
          </Button>
          <Button onClick={() => submit(true)} disabled={saving}
                  data-testid="save-publish-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            {initial?.status === 'published' ? 'Save' : 'Save & publish'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ============== ANNOUNCEMENTS ==============
const TeacherAnnouncements = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', body: '', class_ids: [], priority: 'normal', send_email: false });

  const { data, isLoading } = useQuery({
    queryKey: ['teacher-portal-announcements', tenantId],
    queryFn: () => portalApi('/announcements'),
  });

  const { data: classes = [] } = useQuery({
    queryKey: ['teacher-my-classes-ann', tenantId],
    queryFn: () => portalApi('/teacher/classes'),
  });

  const createMutation = useMutation({
    mutationFn: (d) => {
      const payload = { ...d, audience: d.class_ids.length > 0 ? 'class' : 'school' };
      return fetch(`${API_URL}/api/school/tenants/${tenantId}/teacher-announcement`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).then(r => r.ok ? r.json() : r.json().then(e => { throw new Error(e.detail); }));
    },
    onSuccess: (result) => {
      toast.success(`Announcement posted${result.email_result?.sent ? ` (${result.email_result.sent} emails sent)` : ''}`);
      queryClient.invalidateQueries({ queryKey: ['teacher-portal-announcements'] });
      setShowForm(false);
      setForm({ title: '', body: '', class_ids: [], priority: 'normal', send_email: false });
    },
    onError: (e) => toast.error(e.message),
  });

  const announcements = data?.announcements || [];
  const unreadCount = data?.unread_count || 0;

  const markRead = useMutation({
    mutationFn: (id) => portalApi(`/announcements/${id}/read`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['teacher-portal-announcements'] }),
  });

  return (
    <div className="space-y-4" data-testid="teacher-announcements">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold">Announcements</h2>
          {unreadCount > 0 && <Badge className="bg-red-500 text-white">{unreadCount} new</Badge>}
        </div>
        <Button size="sm" onClick={() => setShowForm(true)} data-testid="teacher-new-announcement-btn"><Plus className="h-4 w-4 mr-1" /> Post Announcement</Button>
      </div>
      {isLoading ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : announcements.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-8 text-center text-slate-400">No announcements</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {announcements.map(a => (
            <Card key={a.id} className={!a.is_read ? 'border-indigo-200 bg-indigo-50/30' : ''} onClick={() => !a.is_read && markRead.mutate(a.id)}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      {!a.is_read && <div className="w-2 h-2 rounded-full bg-indigo-500" />}
                      <h3 className="font-semibold text-sm">{a.title}</h3>
                      {a.priority === 'urgent' && <Badge className="bg-red-100 text-red-700 text-xs">Urgent</Badge>}
                      {a.priority === 'high' && <Badge className="bg-orange-100 text-orange-700 text-xs">High</Badge>}
                    </div>
                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{a.body}</p>
                    <p className="text-xs text-slate-400 mt-2">By {a.author_name} | {new Date(a.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Post Class Announcement</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Title</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="teacher-ann-title" /></div>
            <div><Label>Message</Label><Textarea value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} rows={4} data-testid="teacher-ann-body" /></div>
            <div>
              <Label>Target Classes (leave empty for all)</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {classes.map(c => (
                  <button key={c.id} onClick={() => setForm(f => ({ ...f, class_ids: f.class_ids.includes(c.id) ? f.class_ids.filter(x => x !== c.id) : [...f.class_ids, c.id] }))}
                    className={`px-2 py-1 rounded text-xs border ${form.class_ids.includes(c.id) ? 'bg-indigo-100 border-indigo-300 text-indigo-700' : 'bg-white border-slate-200'}`}>
                    {c.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.send_email} onCheckedChange={v => setForm({ ...form, send_email: v })} />
              <Label className="text-sm">Also send via email</Label>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => createMutation.mutate(form)} disabled={!form.title || !form.body || createMutation.isPending} data-testid="teacher-post-ann-btn">
              {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />} Post
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== AT-RISK STUDENTS ==============
const RISK_COLORS = { critical: 'bg-red-100 text-red-700 border-red-200', high: 'bg-orange-100 text-orange-700 border-orange-200', medium: 'bg-amber-100 text-amber-700 border-amber-200' };

const TeacherAtRisk = ({ tenantId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['teacher-at-risk', tenantId],
    queryFn: () => portalApi('/teacher/at-risk'),
  });

  if (isLoading) return <Loader2 className="h-6 w-6 animate-spin mx-auto" />;

  const students = data?.students || [];

  return (
    <div className="space-y-4" data-testid="teacher-at-risk">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">At-Risk Students</h2>
        <Badge variant="outline">{data?.total_at_risk || 0} flagged</Badge>
      </div>
      {students.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-8 text-center text-green-600 font-medium">No at-risk students in your classes</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {students.map(s => (
            <Card key={s.student_id} className={`border ${RISK_COLORS[s.risk_level] || ''}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    <span className="font-semibold">{s.name}</span>
                    <span className="text-xs text-slate-500">{s.admission_number}</span>
                  </div>
                  <Badge className={RISK_COLORS[s.risk_level]}>{s.risk_level}</Badge>
                </div>
                <div className="flex gap-4 text-sm text-slate-600">
                  <span>Class: {s.class_name}</span>
                  <span>Attendance: {s.attendance_rate}%</span>
                  {s.avg_grade != null && <span>Grade: {s.avg_grade}%</span>}
                </div>
                <div className="mt-2 space-y-0.5">
                  {s.risk_factors.map((f, i) => (
                    <p key={`risk-${f}-${i}`} className="text-xs text-slate-500"><AlertTriangle className="h-3 w-3 inline mr-1" />{f}</p>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};


// ============== MAIN TEACHER PORTAL ==============
// ============== REPORT CONCERN PAGE (direct surface) ==============
const TeacherReportConcern = ({ tenantId }) => {
  const [selectedClass, setSelectedClass] = useState('');
  const [search, setSearch] = useState('');
  const { data: classes = [] } = useQuery({
    queryKey: ['teacher-rc-classes', tenantId],
    queryFn: () => portalApi('/teacher/classes'),
  });
  const { data: students = [] } = useQuery({
    queryKey: ['teacher-rc-students', tenantId, selectedClass],
    queryFn: () => portalApi(`/teacher/classes/${selectedClass}/students`),
    enabled: !!selectedClass,
  });
  const filtered = students.filter((s) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (`${s.first_name} ${s.last_name}`.toLowerCase().includes(q)
      || (s.admission_number || '').toLowerCase().includes(q));
  });
  return (
    <div className="space-y-4" data-testid="teacher-report-concern">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-500" /> Report a Concern
        </h2>
      </div>
      <Card className="border-amber-200 bg-amber-50/50">
        <CardContent className="p-4 text-sm text-amber-900">
          File a behaviour/discipline incident or a safeguarding concern while it's still fresh.
          Low-severity behaviour stays with the form teacher; medium+ auto-notifies parents;
          safeguarding is routed confidentially to the Designated Safeguarding Lead.
        </CardContent>
      </Card>
      <div className="grid md:grid-cols-[260px_1fr] gap-3">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Pick a class</CardTitle></CardHeader>
          <CardContent className="space-y-1">
            {classes.length === 0 ? (
              <p className="text-xs text-slate-400">No classes assigned.</p>
            ) : classes.map((c) => (
              <button key={c.id}
                onClick={() => setSelectedClass(c.id)}
                className={cn('w-full text-left px-2 py-1.5 rounded text-sm',
                  selectedClass === c.id ? 'bg-indigo-600 text-white' : 'hover:bg-slate-100')}
                data-testid={`rc-class-${c.id}`}>
                {c.name} <span className="text-[10px] opacity-70">· {c.student_count}</span>
              </button>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-sm">Students</CardTitle>
              <Input placeholder="Search name or admission #" value={search}
                onChange={(e) => setSearch(e.target.value)} className="h-8 max-w-xs"
                data-testid="rc-student-search" />
            </div>
          </CardHeader>
          <CardContent>
            {!selectedClass ? (
              <p className="text-sm text-slate-400 text-center py-6">Pick a class on the left to see students.</p>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">No students match.</p>
            ) : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Student</TableHead>
                  <TableHead>Admission #</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {filtered.map((s) => (
                    <TableRow key={s.id} data-testid={`rc-student-row-${s.id}`}>
                      <TableCell className="font-medium">{s.first_name} {s.last_name}</TableCell>
                      <TableCell className="font-mono text-xs">{s.admission_number}</TableCell>
                      <TableCell className="text-right">
                        <ReportConcernButton tenantId={tenantId} student={s} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};


const TeacherPortal = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuthStore();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const { data: tenant, isLoading } = useQuery({
    queryKey: ['school-tenant-public', tenantId],
    queryFn: async () => { const res = await fetch(`${API_URL}/api/school/tenants/${tenantId}/public`); return res.ok ? res.json() : null; },
  });

  if (isLoading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-indigo-500" /></div>;

  const brandColor = tenant?.primary_color || '#6366f1';

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':     return <TeacherDashboardV2 onNavigate={setActiveTab} />;
      case 'subjects':      return <TeacherSubjects />;
      case 'lesson-plans':  return <TeacherLessonPlans />;
      case 'students':      return <TeacherStudents />;
      case 'attendance':    return <TeacherAttendance tenantId={tenantId} />;
      case 'classes':       return <TeacherClasses tenantId={tenantId} />;
      case 'timetable':     return <TeacherTimetable tenantId={tenantId} />;
      case 'assignments':   return <TeacherAssignments tenantId={tenantId} />;
      case 'exams':         return <TeacherExamsV2 />;
      case 'question-bank': return <TeacherQuestionBank tenantId={tenantId} />;
      case 'results':       return <TeacherResults />;
      case 'materials':     return <TeacherMaterials />;
      case 'announcements': return <TeacherAnnouncementsV2 tenantId={tenantId} />;
      case 'bible':         return <TeacherBible tenantId={tenantId} />;
      case 'live':          return <TeacherLiveClasses tenantId={tenantId} />;
      case 'reports':       return <TeacherReportsV2 />;
      case 'activity':      return <TeacherActivity />;
      case 'at-risk':       return <TeacherAtRisk tenantId={tenantId} />;
      case 'report':        return <TeacherReportConcern tenantId={tenantId} />;
      default:              return <TeacherDashboardV2 onNavigate={setActiveTab} />;
    }
  };

  return (
    <TooltipProvider delayDuration={0}>
      <div className="flex h-screen bg-slate-50" data-testid="teacher-portal">
        {/* Mobile overlay backdrop */}
        {mobileOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 md:hidden" 
            onClick={() => setMobileOpen(false)}
            data-testid="teacher-mobile-overlay"
          />
        )}
        <div className={cn(
          "flex flex-col h-full bg-slate-900 text-white transition-all duration-300 shrink-0",
          collapsed ? "w-[68px]" : "w-full max-w-[220px]",
          // Mobile: fixed position, hidden by default, shown when mobileOpen
          "fixed md:relative z-50 md:z-auto",
          mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        )}>
          <div className="p-4 border-b border-slate-800">
            <div className="flex items-center justify-between">
              {!collapsed && (
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: brandColor }}>
                    <GraduationCap className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-sm">{tenant?.name}</p>
                    <p className="text-[10px] text-slate-400">Teacher Portal</p>
                  </div>
                </div>
              )}
              <Button variant="ghost" size="icon" onClick={() => setCollapsed(!collapsed)} className="text-slate-400 hover:text-white hover:bg-slate-800 hidden md:flex">
                {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
              </Button>
              <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)} className="text-slate-400 hover:text-white hover:bg-slate-800 md:hidden" data-testid="teacher-mobile-close">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <ScrollArea className="flex-1 py-2">
            <div className="px-2 space-y-0.5">
              {MENU.map(item => (
                <Tooltip key={item.id}>
                  <TooltipTrigger asChild>
                    <button onClick={() => { setActiveTab(item.id); setMobileOpen(false); }}
                      className={cn("w-full flex items-center gap-3 rounded-lg text-sm transition-colors",
                        collapsed ? "justify-center p-2.5" : "px-3 py-2",
                        activeTab === item.id ? "bg-indigo-600 text-white" : "text-slate-400 hover:text-white hover:bg-slate-800")}
                      data-testid={`teacher-nav-${item.id}`}>
                      <item.icon className="h-4 w-4 shrink-0" />
                      {!collapsed && <span>{item.label}</span>}
                    </button>
                  </TooltipTrigger>
                  {collapsed && <TooltipContent side="right">{item.label}</TooltipContent>}
                </Tooltip>
              ))}
            </div>
          </ScrollArea>

          <div className="p-3 border-t border-slate-800">
            <Button variant="ghost" size={collapsed ? "icon" : "sm"} onClick={() => { logout(); navigate(`/school/${tenantId}/login`); }}
              className={cn("text-slate-400 hover:text-white hover:bg-slate-800", !collapsed && "w-full justify-start gap-2")}
              data-testid="teacher-logout-btn">
              <LogOut className="h-4 w-4" />{!collapsed && <span>Logout</span>}
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-auto min-w-0">
          {/* Mobile header with hamburger */}
          <div className="md:hidden flex items-center gap-3 px-3 py-2 bg-white border-b sticky top-0 z-30" data-testid="teacher-mobile-header">
            <Button variant="ghost" size="icon" onClick={() => setMobileOpen(true)} data-testid="teacher-mobile-menu-btn">
              <Menu className="h-5 w-5" />
            </Button>
            <span className="font-semibold text-sm truncate flex-1">{tenant?.name}</span>
            <PortalNotificationBell onClick={() => setActiveTab('announcements')} className="text-slate-600" />
          </div>
          {sessionStorage.getItem('school_preview_token') && (
            <div className="bg-amber-500 text-white px-4 py-2 text-sm text-center font-medium flex items-center justify-center gap-2" data-testid="preview-banner">
              <AlertTriangle className="h-4 w-4" /> Admin Preview Mode — Viewing as Teacher
              <button onClick={() => { sessionStorage.removeItem('school_preview_token'); window.close(); }} className="ml-4 underline hover:no-underline">Exit Preview</button>
            </div>
          )}
          <div className="hidden md:flex sticky top-0 z-10 bg-white/80 backdrop-blur-sm border-b px-6 py-3 justify-end">
            <PortalNotificationBell onClick={() => setActiveTab('announcements')} className="text-slate-600" />
          </div>
          <div className="p-3 sm:p-4 md:p-6 max-w-5xl mx-auto">{renderContent()}</div>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default TeacherPortal;
